import { useState } from "react";
import { motion } from "framer-motion";
import { Plus, X } from "lucide-react";
import { Slider } from "../ui/slider";
import { DECK_THEME as T, fmt$, fmtPct } from "../../lib/deckModel";
import { Card, CardTitle } from "./DeckUI";
import { Editable, useDeckEdit } from "./EditCtx";
import { Callout } from "./MurrietaPages";

const mono = { fontFamily: T.mono };
export const DEFAULT_CAP_OPTIONS = [
  { rate: 5.5, label: "Aggressive" }, { rate: 6.0, label: "Market low" }, { rate: 6.5, label: "Partner base" },
  { rate: 7.0, label: "Conservative" }, { rate: 7.5, label: "Stress test" },
];
const same = (x, y) => Math.abs(x - y) < 0.001;
const outcome = (m, rate) => {
  const value = rate > 0 && m.noi > 0 ? m.noi / (rate / 100) : 0;
  const profit = value - m.totalCost - value * m.saleCostPct;
  return { value, profit, poc: m.totalCost > 0 ? profit / m.totalCost : null };
};

function RateInput({ rate, onCommit, testid }) {
  const [v, setV] = useState(null);
  const commit = () => { if (v !== null) { const x = Number(v); if (Number.isFinite(x) && x > 0) onCommit(Math.round(x * 100) / 100); setV(null); } };
  return <input value={v ?? rate} inputMode="decimal" onChange={(e) => setV(e.target.value.replace(/[^0-9.]/g, ""))} onBlur={commit} onKeyDown={(e) => e.key === "Enter" && e.currentTarget.blur()}
    className="w-full rounded border px-2 py-1 text-xs text-right" style={{ borderColor: T.line }} data-testid={testid} />;
}

function CapOption({ o, i, on, m, pick, upd, remove }) {
  const { editing } = useDeckEdit();
  const r = outcome(m, o.rate);
  const select = () => pick(o.rate);
  return (
    <div className="relative">
      <div role="button" tabIndex={0} onClick={select} onKeyDown={(e) => { if (e.target === e.currentTarget && (e.key === "Enter" || e.key === " ")) { e.preventDefault(); select(); } }} aria-pressed={on} data-testid={`cap-option-${i}`}
        className="w-full text-left rounded-2xl border px-4 py-4 cursor-pointer outline-none focus-visible:ring-2 transition-[background-color,border-color,transform] duration-200 hover:-translate-y-0.5"
        style={{ borderColor: on ? T.navy : T.line, background: on ? T.navy : "#fff" }}>
        <div onClick={(e) => editing && e.stopPropagation()} className={`text-[10px] tracking-[0.18em] min-h-4 truncate ${editing ? "" : "uppercase"}`} style={{ color: on ? "rgba(255,255,255,0.65)" : T.muted, ...mono }}>
          <Editable value={o.label} onChange={(v) => upd(i, { label: v })} placeholder="Label" testid={`cap-option-label-${i}`} />
        </div>
        <div className="mt-2 text-3xl tabular-nums" style={{ color: on ? "#fff" : T.navy, fontFamily: T.serif }}>{Number(o.rate).toFixed(2)}%</div>
        <div className="mt-3 text-xs tabular-nums" style={{ color: on ? "rgba(255,255,255,0.75)" : T.charcoal }}>Value {fmt$(r.value)}</div>
        <div className="text-xs tabular-nums" style={{ color: on ? "#9ED8D0" : r.profit < 0 ? "#B4532A" : T.accent }}>Profit {fmt$(r.profit)}</div>
      </div>
      {editing && (
        <div className="mt-1.5 flex items-center gap-1">
          <RateInput rate={o.rate} onCommit={(rate) => upd(i, { rate })} testid={`cap-option-rate-${i}`} />
          <span className="text-xs" style={{ color: T.muted }}>%</span>
          <button onClick={() => remove(i)} className="p-1 text-red-600" data-testid={`cap-option-remove-${i}`}><X className="h-3.5 w-3.5" /></button>
        </div>
      )}
    </div>
  );
}

function Sensitivity({ m, options, cap }) {
  const rows = options.map((o) => ({ ...o, ...outcome(m, o.rate) }));
  const max = Math.max(1, ...rows.map((r) => r.value), m.totalCost);
  return (
    <Card testid="valuation-sensitivity">
      <div className="flex items-center justify-between"><CardTitle>Value vs. Development Cost</CardTitle><span className="text-xs -mt-4" style={{ color: T.muted }}>TDC {fmt$(m.totalCost)}</span></div>
      <div className="space-y-4">
        {rows.map((r, i) => { const on = same(r.rate, cap); return (
          <div key={i} data-testid={`valuation-sens-row-${i}`}>
            <div className="flex justify-between text-sm mb-1.5" style={{ color: on ? T.navy : T.charcoal }}>
              <span className={on ? "font-semibold" : ""}>{Number(r.rate).toFixed(2)}% cap</span>
              <span className="tabular-nums">{fmt$(r.value)} <span style={{ color: r.profit < 0 ? "#B4532A" : T.accent }}>· {fmtPct(r.poc)}</span></span>
            </div>
            <div className="relative h-3 rounded-full" style={{ background: T.neutral }}>
              <motion.div className="h-3 rounded-full" initial={false} animate={{ width: `${Math.max(1.5, (r.value / max) * 100)}%` }} transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }} style={{ background: on ? T.navy : "#9FB3C2" }} />
              <div className="absolute -top-1 -bottom-1 w-0.5" style={{ left: `${(m.totalCost / max) * 100}%`, background: "#B4532A" }} />
            </div>
          </div>); })}
      </div>
      <div className="mt-5 flex items-center gap-2 text-xs" style={{ color: T.muted }}><span className="h-3 w-0.5" style={{ background: "#B4532A" }} />Total development cost</div>
    </Card>
  );
}

export function ValuationPage({ deck, m, section, variant = "valuation" }) {
  const { editing, setAssumptions, setContent } = useDeckEdit();
  const a = deck.assumptions || {};
  const c = section.content || {};
  const set = (p) => setContent?.(section.id, p);
  const options = a.cap_options?.length ? a.cap_options : DEFAULT_CAP_OPTIONS;
  const setOptions = (cap_options) => setAssumptions?.({ cap_options });
  const full = variant === "valuation";
  const cap = Number(a.cap_rate_pct) || 0;
  const pick = (rate) => setAssumptions?.({ cap_rate_pct: rate });
  const upd = (i, p) => setOptions(options.map((o, j) => (j === i ? { ...o, ...p } : o)));
  const r = outcome(m, cap);
  const spread = (m.yoc - cap / 100) * 10000;
  return (
    <div className="space-y-6" data-testid="section-valuation">
      <Card>
        <div className="flex items-center justify-between"><CardTitle>Select a Cap Rate</CardTitle><span className="text-xs -mt-4" style={{ color: T.muted }}>Click an option or drag the slider</span></div>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3" data-testid="cap-options">
          {options.map((o, i) => <CapOption key={i} o={o} i={i} on={same(o.rate, cap)} m={m} pick={pick} upd={upd} remove={(k) => setOptions(options.filter((_, j) => j !== k))} />)}
        </div>
        {editing && <button onClick={() => setOptions([...options, { rate: 8, label: "New option" }])} className="mt-3 flex items-center gap-1 text-xs font-medium" style={{ color: T.accent }} data-testid="cap-option-add"><Plus className="h-3.5 w-3.5" />Add cap rate option</button>}
        <div className="mt-8" data-testid="cap-slider">
          <div className="flex justify-between text-sm mb-3"><span style={{ color: T.muted }}>Fine-tune cap rate</span><span className="tabular-nums" style={{ color: T.navy }} data-testid="cap-slider-value">{cap.toFixed(2)}%</span></div>
          <Slider min={4} max={9.5} step={0.05} value={[cap]} onValueChange={([v]) => pick(Math.round(v * 100) / 100)} />
        </div>
      </Card>
      {full && <div className="grid xl:grid-cols-[1.2fr_1fr] gap-6">
        <Card className="!p-0 overflow-hidden" testid="valuation-equation">
          <div className="px-6 pt-6"><CardTitle>Income Approach</CardTitle></div>
          <div className="px-6 pb-6 grid grid-cols-[1fr_auto_1fr] items-end gap-4">
            <div><div className="text-xs" style={{ color: T.muted }}>Stabilized NOI</div><div className="text-3xl tabular-nums" style={{ color: T.navy, fontFamily: T.serif }} data-testid="valuation-noi">{fmt$(m.noi)}</div></div>
            <div className="text-3xl pb-0.5" style={{ color: T.muted, fontFamily: T.serif }}>÷</div>
            <div><div className="text-xs" style={{ color: T.muted }}>Cap rate</div><div className="text-3xl tabular-nums" style={{ color: T.navy, fontFamily: T.serif }}>{cap.toFixed(2)}%</div></div>
          </div>
          <div className="flex items-baseline justify-between gap-4 px-6 py-5 text-white" style={{ background: T.navy }}>
            <span className="text-[11px] uppercase tracking-[0.2em]" style={mono}>Stabilized Value</span>
            <span className="text-4xl tabular-nums" style={{ fontFamily: T.serif }} data-testid="valuation-value">{fmt$(r.value)}</span>
          </div>
          {[["Total development cost", fmt$(m.totalCost)], [`Sale costs (${(m.saleCostPct * 100).toFixed(1)}%)`, fmt$(-r.value * m.saleCostPct)]].map(([k, v]) => (
            <div key={k} className="flex justify-between px-6 py-3.5 border-t text-sm" style={{ borderColor: T.neutral, color: T.charcoal }}><span>{k}</span><span className="tabular-nums">{v}</span></div>
          ))}
          <div className="flex justify-between px-6 py-4 border-t text-sm font-semibold" style={{ borderColor: T.line, color: r.profit < 0 ? "#B4532A" : T.accent }}><span>Development profit</span><span className="tabular-nums" data-testid="valuation-profit">{fmt$(r.profit)}</span></div>
        </Card>
        <div className="grid grid-cols-2 gap-4 content-start">
          {[["Profit on cost", fmtPct(r.poc), "valuation-poc"], ["Yield on cost", fmtPct(m.yoc, 2), "valuation-yoc"],
            ["Development spread", Number.isFinite(spread) && m.totalCost > 0 ? `${Math.round(spread)} bps` : "—", "valuation-spread"], ["Value / TDC", m.totalCost > 0 ? `${(r.value / m.totalCost).toFixed(2)}x` : "—", "valuation-multiple"]].map(([k, v, t]) => (
            <Card key={k} testid={t}><div className="text-sm" style={{ color: T.muted }}>{k}</div><div className="mt-2 text-3xl tabular-nums" style={{ color: T.navy, fontFamily: T.serif }}>{v}</div></Card>
          ))}
        </div>
      </div>}
      <Sensitivity m={m} options={options} cap={cap} />
      <div className="grid lg:grid-cols-2 gap-6">
        {!full && <Callout tone value={c.cap_note ?? "The partner base cap rate reflects current regional evidence for new small-format commercial assets. Each 25 bps move changes stabilized value materially; review the sensitivity before relying on any single number."} onChange={(v) => set({ cap_note: v })} testid="valuation-cap-note" />}
        {full && <Callout tone value={c.value_note ?? "Stabilized value applies the selected cap rate to stabilized NOI. Development profit is value less total development cost and estimated sale costs."} onChange={(v) => set({ value_note: v })} testid="valuation-value-note" />}
        <Callout tag="Preliminary" value={c.not_appraisal ?? "Income-based stabilized value for underwriting only. This is not an appraisal or a broker opinion of value."} onChange={(v) => set({ not_appraisal: v })} testid="valuation-not-appraisal" />
      </div>
    </div>
  );
}
